/* drl-video-reviews — auto-advancing scroll-snap testimonial carousel.
   Native horizontal scroller drives swipe/keyboard; JS adds: timed
   auto-advance (pauses on hover/focus/drag, unmuted clip, hidden tab,
   reduced-motion), in-view-only video playback via IntersectionObserver,
   a single-audio mute toggle, and a scroll-linked progress bar. */

class DrlVideoReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.drl-vr__track');
    if (!this.track) return;

    this.cards = Array.from(this.track.children);
    this.videos = Array.from(this.querySelectorAll('video'));
    this.muteBtns = Array.from(this.querySelectorAll('.drl-vr__mute'));
    this.navs = Array.from(this.querySelectorAll('.drl-vr__nav'));
    this.progressEl = this.querySelector('.drl-vr__progress');

    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.editor = !!(window.Shopify && window.Shopify.designMode);
    this.hovering = false;
    this.dragging = false;
    this.audioActive = false;

    this.onScroll = this.onScroll.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.bind();
    this.setupVideos();
    this.updateProgress();

    if (this.dataset.autoplay === 'true' && !this.reduce && !this.editor && this.cards.length > 1) {
      this.intervalMs = parseInt(this.dataset.interval, 10) || 5000;
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    if (this.io) this.io.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
    this.track.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  bind() {
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);

    this.addEventListener('mouseenter', () => { this.hovering = true; });
    this.addEventListener('mouseleave', () => { this.hovering = false; });
    this.addEventListener('focusin', () => { this.hovering = true; });
    this.addEventListener('focusout', () => { this.hovering = false; });

    this.track.addEventListener('pointerdown', () => { this.dragging = true; });
    const release = () => {
      clearTimeout(this.dragTimer);
      this.dragTimer = setTimeout(() => { this.dragging = false; }, 1200);
    };
    this.track.addEventListener('pointerup', release);
    this.track.addEventListener('pointercancel', release);

    this.navs.forEach((btn) => {
      btn.addEventListener('click', () => this.scrollByCards(parseInt(btn.dataset.dir, 10) || 1));
    });
    this.muteBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.toggleMute(btn));
    });
  }

  // ── auto-advance ──────────────────────────────────────────────────────
  start() { this.stop(); this.timer = setInterval(() => this.tick(), this.intervalMs); }
  stop() { if (this.timer) clearInterval(this.timer); this.timer = null; }

  tick() {
    if (this.hovering || this.dragging || this.audioActive || document.hidden) return;
    this.scrollByCards(1);
  }

  scrollByCards(dir) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    let target = this.track.scrollLeft + dir * this.step();
    if (dir > 0 && this.track.scrollLeft >= max - 4) target = 0;   // loop to start
    else if (target < 0) target = max;                              // wrap to end
    this.track.scrollTo({ left: target, behavior: this.reduce ? 'auto' : 'smooth' });
  }

  step() {
    const card = this.cards[0];
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  // ── video playback (in-view only) ───────────────────────────────────────
  setupVideos() {
    if (!this.videos.length) return;
    if (!('IntersectionObserver' in window)) {
      this.videos.forEach((v) => this.safePlay(v));
      return;
    }
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) this.safePlay(e.target);
        else { e.target.pause(); this.muteVideo(e.target); }
      });
    }, { root: this.track, threshold: 0.6 });
    this.videos.forEach((v) => this.io.observe(v));
  }

  safePlay(v) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }

  // ── mute toggle (one audio at a time) ──────────────────────────────────
  toggleMute(btn) {
    const card = btn.closest('.drl-vr__card');
    const video = card && card.querySelector('video');
    if (!video) return;

    if (video.muted) {
      this.videos.forEach((o) => { if (o !== video) this.muteVideo(o); });
      video.muted = false;
      this.safePlay(video);
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', 'Hang kikapcsolása');
      this.audioActive = true;
      card.scrollIntoView({ behavior: this.reduce ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    } else {
      this.muteVideo(video);
    }
  }

  muteVideo(video) {
    if (!video.muted) video.muted = true;
    const card = video.closest('.drl-vr__card');
    const btn = card && card.querySelector('.drl-vr__mute');
    if (btn) {
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'Hang bekapcsolása');
    }
    this.audioActive = this.videos.some((o) => !o.muted);
  }

  // ── progress + lifecycle ───────────────────────────────────────────────
  onScroll() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => { this.raf = null; this.updateProgress(); });
  }

  updateProgress() {
    if (!this.progressEl) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const ratio = max > 0 ? this.track.scrollLeft / max : 0;
    const visible = this.track.scrollWidth > 0 ? this.track.clientWidth / this.track.scrollWidth : 1;
    const bar = this.progressEl.clientWidth;
    const thumbW = Math.max(24, bar * visible);
    this.style.setProperty('--drl-vr-thumb', thumbW + 'px');
    this.style.setProperty('--drl-vr-thumb-x', ratio * (bar - thumbW) + 'px');
  }

  onVisibility() {
    if (document.hidden) this.videos.forEach((v) => v.pause());
    else this.videos.forEach((v) => { if (v.muted) this.safePlay(v); });
  }
}

if (!customElements.get('drl-video-reviews')) {
  customElements.define('drl-video-reviews', DrlVideoReviews);
}
